<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useAccount } from 'dashboard/composables/useAccount';
import { useEmitter } from 'dashboard/composables/emitter';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import SegmentedControl from 'dashboard/components-next/segmented-control/SegmentedControl.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import TemplateChatPreview from './components/TemplateChatPreview.vue';
import TemplateButtonsEditor from './components/TemplateButtonsEditor.vue';
import TemplateDraftAssistant from './components/TemplateDraftAssistant.vue';
import PhraseShortcuts from './components/PhraseShortcuts.vue';
import {
  buttonFromDraft,
  buttonPayload,
  buttonToDraft,
  exampleUrl,
  insertButton,
  isButtonComplete,
  withinButtonLimits,
} from './helpers/templateButtons';
import { bodyVariablesError } from './helpers/templateVariables';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { formatTemplateLanguage } from '../templates/templateUtils';

// Meta's documented set of supported message-template locales.
const LANGUAGE_CODES = [
  'af',
  'sq',
  'ar',
  'az',
  'bn',
  'bg',
  'ca',
  'zh_CN',
  'zh_HK',
  'zh_TW',
  'hr',
  'cs',
  'da',
  'nl',
  'en',
  'en_GB',
  'en_US',
  'et',
  'fil',
  'fi',
  'fr',
  'ka',
  'de',
  'el',
  'gu',
  'ha',
  'he',
  'hi',
  'hu',
  'id',
  'ga',
  'it',
  'ja',
  'kn',
  'kk',
  'ko',
  'lo',
  'lv',
  'lt',
  'mk',
  'ms',
  'ml',
  'mr',
  'nb',
  'fa',
  'pl',
  'pt_BR',
  'pt_PT',
  'pa',
  'ro',
  'ru',
  'sr',
  'sk',
  'sl',
  'es',
  'es_AR',
  'es_ES',
  'es_MX',
  'sw',
  'sv',
  'ta',
  'te',
  'th',
  'tr',
  'uk',
  'ur',
  'uz',
  'vi',
  'zu',
]; // eslint-disable-line prettier/prettier

const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const HEADER_LIMIT = 60;
const BODY_LIMIT = 1024;
const FOOTER_LIMIT = 60;

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { captainTasksEnabled } = useCaptain();
const { currentAccount } = useAccount();

// Opened from an inbox, the template belongs to that inbox. Opened from the account-wide templates
// page, the admin picks which WhatsApp account it is submitted to.
const isInboxLocked = Boolean(route.params.inboxId);

// Only WhatsApp Cloud inboxes can create templates here; 360dialog and Twilio manage them upstream.
const eligibleInboxes = computed(() =>
  store.getters['inboxes/getInboxes'].filter(
    item =>
      item.channel_type === INBOX_TYPES.WHATSAPP &&
      item.provider === 'whatsapp_cloud'
  )
);

const initialInboxId = () => {
  if (isInboxLocked) return String(route.params.inboxId);
  const requested = route.query.inbox_id && String(route.query.inbox_id);
  const ids = eligibleInboxes.value.map(item => String(item.id));
  if (ids.includes(requested)) return requested;
  return ids.length === 1 ? ids[0] : '';
};

const inboxId = ref(initialInboxId());
// A hard refresh on this page renders before the inbox list arrives.
watch(eligibleInboxes, () => {
  if (!inboxId.value) inboxId.value = initialInboxId();
});
const inbox = computed(() =>
  inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : null
);

const goToTemplatesTab = () =>
  isInboxLocked
    ? router.push({
        name: 'settings_inbox_show',
        params: { inboxId: inboxId.value, tab: 'templates' },
      })
    : router.push({ name: 'settings_templates' });

const STEPS = computed(() => [
  t('WHATSAPP_TEMPLATE_MGMT.CREATE.STEPS.BASICS'),
  t('WHATSAPP_TEMPLATE_MGMT.CREATE.STEPS.CONTENT'),
  t('WHATSAPP_TEMPLATE_MGMT.CREATE.STEPS.BUTTONS'),
  t('WHATSAPP_TEMPLATE_MGMT.CREATE.STEPS.REVIEW'),
]);

const STEP_HINTS = computed(() => [
  t('WHATSAPP_TEMPLATE_MGMT.CREATE.STEP_HINTS.BASICS'),
  t('WHATSAPP_TEMPLATE_MGMT.CREATE.STEP_HINTS.CONTENT'),
  t('WHATSAPP_TEMPLATE_MGMT.CREATE.STEP_HINTS.BUTTONS'),
  t('WHATSAPP_TEMPLATE_MGMT.CREATE.STEP_HINTS.REVIEW'),
]);

const languageOptions = LANGUAGE_CODES.map(code => ({
  value: code,
  label: formatTemplateLanguage(code),
})).sort((first, second) => first.label.localeCompare(second.label));

// Most templates are written in the language the team works in.
const languageFor = uiLocale =>
  LANGUAGE_CODES.includes(uiLocale) ? uiLocale : 'en_US';

const buildDraft = () => ({
  name: '',
  category: 'MARKETING',
  language: languageFor(locale.value),
  headerFormat: 'NONE',
  headerText: '',
  bodyText: '',
  footerText: '',
  buttons: [],
});

const currentStep = ref(0);
const isSubmitting = ref(false);
const isAssistantOpen = ref(false);
// A chat turn in flight: the chat can't be left until it lands, or the draft it brings would overwrite
// whatever is edited in the form meanwhile.
const isAssistantBusy = ref(false);
const draft = reactive(buildDraft());
const variableExamples = reactive({});

// On a hard refresh the account's locale is applied after this page is set up.
watch(locale, (uiLocale, previousLocale) => {
  if (draft.language === languageFor(previousLocale)) {
    draft.language = languageFor(uiLocale);
  }
});

const isFirstStep = computed(() => currentStep.value === 0);
const isLastStep = computed(() => currentStep.value === STEPS.value.length - 1);

const bodyVariables = computed(() => {
  const matches = [...draft.bodyText.matchAll(/\{\{\s*(\d+)\s*\}\}/g)];
  return [...new Set(matches.map(match => match[1]))].sort(
    (first, second) => Number(first) - Number(second)
  );
});

const insertVariable = () => {
  const nextIndex =
    bodyVariables.value.length > 0
      ? Math.max(...bodyVariables.value.map(Number)) + 1
      : 1;
  draft.bodyText = `${draft.bodyText}${draft.bodyText && !draft.bodyText.endsWith(' ') ? ' ' : ''}{{${nextIndex}}}`;
};

const variablesError = computed(() =>
  bodyVariablesError(bodyVariables.value, variableExamples)
);

const nameError = computed(() => {
  if (!draft.name) return '';
  return /^[a-z0-9_]+$/.test(draft.name)
    ? ''
    : t('WHATSAPP_TEMPLATE_MGMT.CREATE.BASICS.NAME_ERROR');
});

const isStepValid = step => {
  if (step === 0) {
    return Boolean(
      inboxId.value &&
        draft.name.trim() &&
        !nameError.value &&
        draft.category &&
        draft.language
    );
  }

  if (step === 1) {
    const hasValidHeader =
      draft.headerFormat !== 'TEXT' ||
      (draft.headerText.trim() && draft.headerText.length <= HEADER_LIMIT);

    return Boolean(
      draft.bodyText.trim() &&
        draft.bodyText.length <= BODY_LIMIT &&
        draft.footerText.length <= FOOTER_LIMIT &&
        hasValidHeader &&
        !variablesError.value
    );
  }

  if (step === 2) {
    return (
      withinButtonLimits(draft.buttons) &&
      draft.buttons.every(button => isButtonComplete(button, draft.category))
    );
  }

  return true;
};

const canContinue = computed(() => isStepValid(currentStep.value));

// The template in the shape Captain drafts it (Captain::TemplateDraftSchema), so each turn starts
// from what the form holds now, hand edits included.
const assistantTemplate = computed(() => ({
  name: draft.name,
  category: draft.category,
  language: draft.language,
  header_text: draft.headerFormat === 'TEXT' ? draft.headerText : '',
  body_text: draft.bodyText,
  body_examples: bodyVariables.value.map(
    variable => variableExamples[variable] || ''
  ),
  footer_text: draft.footerText,
  buttons: draft.buttons.map(buttonToDraft),
}));

// A part the provider left out or sent as null applies empty; the steps' validation then holds the
// form on whatever that leaves missing, such as a variable with no example.
const applyAssistantTemplate = template => {
  draft.name = template.name || '';
  if (template.category) draft.category = template.category;
  if (LANGUAGE_CODES.includes(template.language)) {
    draft.language = template.language;
  }
  draft.headerFormat = template.header_text ? 'TEXT' : 'NONE';
  draft.headerText = template.header_text || '';
  draft.bodyText = template.body_text || '';
  draft.footerText = template.footer_text || '';
  draft.buttons = (template.buttons || [])
    .map(buttonFromDraft)
    .reduce(insertButton, []);
  const examples = template.body_examples || [];
  Object.keys(variableExamples).forEach(key => delete variableExamples[key]);
  bodyVariables.value.forEach(variable => {
    variableExamples[variable] = examples[Number(variable) - 1] || '';
  });
};

// The furthest step that can be opened: the first one still missing something, or the review.
const firstOpenStep = computed(
  () => [0, 1, 2].find(step => !isStepValid(step)) ?? STEPS.value.length - 1
);

// Any step up to that one, from the steps on top, the preview or the chat (once its turn landed).
// Asking for a later one lands on what is still missing.
const goToStep = step => {
  if (isAssistantBusy.value) return;
  currentStep.value = Math.min(step, firstOpenStep.value);
  isAssistantOpen.value = false;
};

// Leaving the chat goes through the same guard: a draft applied while it was open may have left the
// step the form was on, or one before it, incomplete.
const toggleAssistant = () => {
  if (isAssistantOpen.value) goToStep(currentStep.value);
  else isAssistantOpen.value = true;
};

// The floating Captain launcher opens this page's assistant.
useEmitter(BUS_EVENTS.TOGGLE_TEMPLATE_ASSISTANT, toggleAssistant);

const PREVIEW_PART_STEPS = { header: 1, body: 1, footer: 1, buttons: 2 };

// Ready-made headers and footers, in the template's spirit: a utility one never sounds like an offer.
const headerPhrases = computed(() =>
  draft.category === 'MARKETING'
    ? [
        t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.HEADER.OFFER'),
        t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.HEADER.NEWS'),
        t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.HEADER.LAST_DAYS'),
        t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.HEADER.GIFT'),
      ]
    : [
        t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.HEADER.PAYMENT'),
        t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.HEADER.ORDER'),
        t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.HEADER.APPOINTMENT'),
        t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.HEADER.NOTICE'),
      ]
);

const footerPhrases = computed(() =>
  [
    ...(draft.category === 'MARKETING'
      ? [
          t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.FOOTER.OPT_OUT'),
          t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.FOOTER.STOCK'),
        ]
      : [
          t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.FOOTER.AUTOMATIC'),
          t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.FOOTER.HOURS'),
        ]),
    t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.FOOTER.REPLY'),
    t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.FOOTER.TEAM', {
      name: currentAccount.value?.name,
    }),
  ].filter(phrase => phrase.length <= FOOTER_LIMIT)
);

// A phrase being pointed at, shown in the preview before it is applied.
const phrasePreview = ref(null);

const applyHeaderPhrase = phrase => {
  draft.headerFormat = 'TEXT';
  draft.headerText = phrase;
};

const previewHeaderText = computed(() => {
  if (phrasePreview.value?.part === 'header') return phrasePreview.value.text;
  return draft.headerFormat === 'TEXT' ? draft.headerText : '';
});

const previewFooterText = computed(() =>
  phrasePreview.value?.part === 'footer'
    ? phrasePreview.value.text
    : draft.footerText
);

const previewPhrase = (part, text, index) => {
  phrasePreview.value = text ? { part, text, index } : null;
};

const previewBodyParts = computed(() => {
  if (!draft.bodyText) return [];

  const parts = [];
  const regex = /\{\{\s*(\d+)\s*\}\}/g;
  let lastIndex = 0;
  let match = regex.exec(draft.bodyText);

  while (match) {
    if (match.index > lastIndex) {
      parts.push({
        value: draft.bodyText.slice(lastIndex, match.index),
        variable: false,
      });
    }
    const example = variableExamples[match[1]];
    parts.push({
      value: example && example.trim() ? example : `[${match[1]}]`,
      variable: true,
    });
    lastIndex = regex.lastIndex;
    match = regex.exec(draft.bodyText);
  }

  if (lastIndex < draft.bodyText.length) {
    parts.push({ value: draft.bodyText.slice(lastIndex), variable: false });
  }

  return parts;
});

const previewButtons = computed(() =>
  draft.buttons
    .map((button, index) => {
      const pointed =
        phrasePreview.value?.part === 'button' &&
        phrasePreview.value.index === index;
      return {
        type: button.type,
        text:
          button.type === 'COPY_CODE'
            ? t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.COPY_CODE_TEXT')
            : (pointed && phrasePreview.value.text) || button.text,
        link: button.type === 'URL' ? exampleUrl(button) : '',
        highlight: pointed,
      };
    })
    .filter(button => button.text.trim())
);

const goBack = () => {
  if (!isFirstStep.value) currentStep.value -= 1;
};

const goNext = () => {
  if (!canContinue.value) return;
  if (!isLastStep.value) currentStep.value += 1;
};

const buildComponents = () => {
  const components = [];

  if (draft.headerFormat === 'TEXT') {
    components.push({
      type: 'HEADER',
      format: 'TEXT',
      text: draft.headerText.trim(),
    });
  }

  const bodyComponent = { type: 'BODY', text: draft.bodyText.trim() };
  if (bodyVariables.value.length) {
    bodyComponent.example = {
      body_text: [
        bodyVariables.value.map(variable => variableExamples[variable]),
      ],
    };
  }
  components.push(bodyComponent);

  if (draft.footerText.trim()) {
    components.push({ type: 'FOOTER', text: draft.footerText.trim() });
  }

  if (draft.buttons.length) {
    components.push({
      type: 'BUTTONS',
      buttons: draft.buttons.map(buttonPayload),
    });
  }

  return components;
};

const submit = async () => {
  if (isSubmitting.value) return;
  // Meta answers an incomplete template with a 400; the step still missing something says what.
  if (firstOpenStep.value < STEPS.value.length - 1) {
    goToStep(firstOpenStep.value);
    return;
  }

  isSubmitting.value = true;
  try {
    await InboxesAPI.createMessageTemplate(inboxId.value, {
      name: draft.name.trim(),
      category: draft.category,
      language: draft.language,
      components: buildComponents(),
    });
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.CREATE.SUCCESS'));
    goToTemplatesTab();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full">
    <div class="flex-1 overflow-y-auto">
      <div class="flex flex-col w-full gap-6 py-6 mx-auto max-w-7xl">
        <header class="flex flex-col gap-3">
          <nav
            class="flex flex-wrap items-center gap-1.5 text-sm text-n-slate-11"
          >
            <NextButton
              v-tooltip="t('WHATSAPP_TEMPLATE_MGMT.CREATE.BREADCRUMB.BACK')"
              type="button"
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-arrow-left"
              class="-ms-1.5"
              @click="goToTemplatesTab"
            />
            <template v-if="isInboxLocked">
              <button
                type="button"
                class="p-0 transition-colors hover:text-n-slate-12 hover:underline"
                @click="router.push({ name: 'settings_inbox_list' })"
              >
                {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.BREADCRUMB.INBOXES') }}
              </button>
              <Icon icon="i-lucide-chevron-right" class="size-3.5" />
            </template>
            <button
              type="button"
              class="p-0 transition-colors hover:text-n-slate-12 hover:underline"
              @click="goToTemplatesTab"
            >
              {{
                isInboxLocked && inbox
                  ? t(
                      'WHATSAPP_TEMPLATE_MGMT.CREATE.BREADCRUMB.INBOX_TEMPLATES',
                      {
                        inbox: inbox.name,
                      }
                    )
                  : t('WHATSAPP_TEMPLATE_MGMT.CREATE.BREADCRUMB.TEMPLATES')
              }}
            </button>
            <Icon icon="i-lucide-chevron-right" class="size-3.5" />
            <span class="text-n-slate-12">
              {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.BREADCRUMB.NEW') }}
            </span>
          </nav>
          <div class="flex flex-wrap items-end justify-between gap-3">
            <div class="flex flex-col gap-1">
              <h1 class="m-0 text-2xl font-semibold text-n-slate-12">
                {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.TITLE') }}
              </h1>
              <p class="m-0 text-sm text-n-slate-11">
                {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.SUBTITLE') }}
              </p>
            </div>
            <NextButton
              v-if="captainTasksEnabled"
              type="button"
              :variant="isAssistantOpen ? 'faded' : 'solid'"
              :color="isAssistantOpen ? 'slate' : 'blue'"
              size="sm"
              :icon="
                isAssistantOpen ? 'i-lucide-list-checks' : 'i-lucide-sparkles'
              "
              :disabled="isAssistantBusy"
              @click="toggleAssistant"
            >
              {{
                isAssistantOpen
                  ? t('WHATSAPP_TEMPLATE_MGMT.CREATE.ASSISTANT.CLOSE')
                  : t('WHATSAPP_TEMPLATE_MGMT.CREATE.ASSISTANT.OPEN')
              }}
            </NextButton>
          </div>
        </header>

        <div class="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr),360px]">
          <div
            class="min-w-0 rounded-xl bg-n-solid-1 outline outline-1 outline-n-container -outline-offset-1"
          >
            <!-- Wizard: every step up to the first one still missing something can be opened. -->
            <ol
              v-show="!isAssistantOpen"
              class="grid grid-cols-4 gap-3 px-6 pt-5 pb-4 m-0 list-none border-b border-n-weak"
            >
              <li v-for="(step, index) in STEPS" :key="step" class="min-w-0">
                <button
                  type="button"
                  class="flex flex-col w-full gap-2 p-0 text-start group disabled:cursor-not-allowed"
                  :disabled="index > firstOpenStep"
                  @click="goToStep(index)"
                >
                  <span
                    class="w-full h-1 transition-colors rounded-full"
                    :class="
                      index < currentStep
                        ? 'bg-n-teal-9'
                        : index === currentStep
                          ? 'bg-n-blue-9'
                          : 'bg-n-slate-4'
                    "
                  />
                  <span class="flex items-center min-w-0 gap-2">
                    <span
                      class="grid flex-shrink-0 text-[11px] font-semibold transition-colors rounded-full size-5 place-content-center"
                      :class="
                        index < currentStep
                          ? 'bg-n-teal-9 text-white'
                          : index === currentStep
                            ? 'bg-n-blue-9 text-white'
                            : 'bg-n-alpha-2 text-n-slate-11'
                      "
                    >
                      <Icon
                        v-if="index < currentStep"
                        icon="i-lucide-check"
                        class="size-3"
                      />
                      <span v-else>{{ index + 1 }}</span>
                    </span>
                    <span
                      class="text-sm truncate transition-colors"
                      :class="
                        index === currentStep
                          ? 'font-medium text-n-slate-12'
                          : index > firstOpenStep
                            ? 'text-n-slate-10'
                            : 'text-n-slate-11 group-hover:text-n-slate-12'
                      "
                    >
                      {{ step }}
                    </span>
                  </span>
                  <span
                    class="hidden text-xs truncate sm:block ps-7"
                    :class="
                      index > firstOpenStep
                        ? 'text-n-slate-10'
                        : 'text-n-slate-11'
                    "
                  >
                    {{ STEP_HINTS[index] }}
                  </span>
                </button>
              </li>
            </ol>

            <div :class="isAssistantOpen ? '' : 'p-6'">
              <!-- Mounted on first open, and kept: the chat survives a trip to the form and back. -->
              <KeepAlive>
                <TemplateDraftAssistant
                  v-if="captainTasksEnabled && isAssistantOpen"
                  :template="assistantTemplate"
                  @apply="applyAssistantTemplate"
                  @busy="isAssistantBusy = $event"
                />
              </KeepAlive>
              <div v-show="!isAssistantOpen">
                <Transition
                  enter-active-class="transition duration-150 ease-out"
                  enter-from-class="opacity-0 translate-x-2"
                  enter-to-class="opacity-100 translate-x-0"
                  mode="out-in"
                >
                  <!-- Step 1: Basics -->
                  <section
                    v-if="currentStep === 0"
                    key="basics"
                    class="flex flex-col gap-5"
                  >
                    <div v-if="!isInboxLocked" class="flex flex-col gap-2">
                      <span class="text-sm font-medium text-n-slate-12">
                        {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.BASICS.INBOX') }}
                      </span>
                      <p class="m-0 text-xs text-n-slate-11">
                        {{
                          t('WHATSAPP_TEMPLATE_MGMT.CREATE.BASICS.INBOX_HELP')
                        }}
                      </p>
                      <div
                        v-if="eligibleInboxes.length"
                        class="grid gap-3 sm:grid-cols-2"
                      >
                        <RadioCard
                          v-for="option in eligibleInboxes"
                          :id="`inbox-${option.id}`"
                          :key="option.id"
                          :label="option.name"
                          :description="option.phone_number || ''"
                          :is-active="inboxId === String(option.id)"
                          @select="inboxId = String(option.id)"
                        />
                      </div>
                      <p
                        v-else
                        class="p-3 m-0 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-11"
                      >
                        {{
                          t('WHATSAPP_TEMPLATE_MGMT.CREATE.BASICS.NO_INBOXES')
                        }}
                      </p>
                    </div>

                    <div class="flex flex-col gap-1">
                      <Input
                        v-model="draft.name"
                        :label="t('WHATSAPP_TEMPLATE_MGMT.CREATE.BASICS.NAME')"
                        :placeholder="
                          t(
                            'WHATSAPP_TEMPLATE_MGMT.CREATE.BASICS.NAME_PLACEHOLDER'
                          )
                        "
                        :message="
                          nameError ||
                          t('WHATSAPP_TEMPLATE_MGMT.CREATE.BASICS.NAME_HELP')
                        "
                        :message-type="nameError ? 'error' : 'info'"
                      />
                    </div>

                    <div class="flex flex-col gap-2">
                      <span class="text-sm font-medium text-n-slate-12">
                        {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.BASICS.CATEGORY') }}
                      </span>
                      <div class="grid gap-3 sm:grid-cols-3">
                        <RadioCard
                          v-for="category in CATEGORIES"
                          :id="category"
                          :key="category"
                          :label="
                            t(
                              `WHATSAPP_TEMPLATE_MGMT.CREATE.BASICS.CATEGORY_${category}_LABEL`
                            )
                          "
                          :description="
                            t(
                              `WHATSAPP_TEMPLATE_MGMT.CREATE.BASICS.CATEGORY_${category}_DESCRIPTION`
                            )
                          "
                          :is-active="draft.category === category"
                          @select="draft.category = category"
                        />
                      </div>
                    </div>

                    <div class="flex flex-col gap-1">
                      <span class="text-sm font-medium text-n-slate-12">
                        {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.BASICS.LANGUAGE') }}
                      </span>
                      <ComboBox
                        v-model="draft.language"
                        :options="languageOptions"
                        :search-placeholder="
                          t(
                            'WHATSAPP_TEMPLATE_MGMT.CREATE.BASICS.LANGUAGE_SEARCH'
                          )
                        "
                      />
                    </div>
                  </section>

                  <!-- Step 2: Content -->
                  <section
                    v-else-if="currentStep === 1"
                    key="content"
                    class="flex flex-col gap-5"
                  >
                    <div class="flex flex-col gap-2">
                      <span class="text-sm font-medium text-n-slate-12">
                        {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.CONTENT.HEADER') }}
                      </span>
                      <SegmentedControl
                        v-model="draft.headerFormat"
                        :options="[
                          {
                            value: 'NONE',
                            label: t(
                              'WHATSAPP_TEMPLATE_MGMT.CREATE.CONTENT.HEADER_NONE'
                            ),
                          },
                          {
                            value: 'TEXT',
                            label: t(
                              'WHATSAPP_TEMPLATE_MGMT.CREATE.CONTENT.HEADER_TEXT'
                            ),
                          },
                        ]"
                      />
                      <Input
                        v-if="draft.headerFormat === 'TEXT'"
                        v-model="draft.headerText"
                        :placeholder="
                          t(
                            'WHATSAPP_TEMPLATE_MGMT.CREATE.CONTENT.HEADER_TEXT_PLACEHOLDER'
                          )
                        "
                        :maxlength="HEADER_LIMIT"
                        :message="`${draft.headerText.length}/${HEADER_LIMIT}`"
                        :message-type="
                          draft.headerText.length > HEADER_LIMIT
                            ? 'error'
                            : 'info'
                        "
                      />
                      <PhraseShortcuts
                        :phrases="headerPhrases"
                        :model-value="
                          draft.headerFormat === 'TEXT' ? draft.headerText : ''
                        "
                        @apply="applyHeaderPhrase"
                        @preview="previewPhrase('header', $event)"
                      />
                    </div>

                    <div class="flex flex-col gap-2">
                      <div class="flex items-center justify-between">
                        <span class="text-sm font-medium text-n-slate-12">
                          {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.CONTENT.BODY') }}
                        </span>
                        <NextButton
                          type="button"
                          variant="faded"
                          color="slate"
                          size="xs"
                          icon="i-lucide-plus"
                          @click="insertVariable"
                        >
                          {{
                            t(
                              'WHATSAPP_TEMPLATE_MGMT.CREATE.CONTENT.ADD_VARIABLE'
                            )
                          }}
                        </NextButton>
                      </div>
                      <TextArea
                        v-model="draft.bodyText"
                        :placeholder="
                          t(
                            'WHATSAPP_TEMPLATE_MGMT.CREATE.CONTENT.BODY_PLACEHOLDER'
                          )
                        "
                        auto-height
                        min-height="6rem"
                        max-height="12rem"
                        show-character-count
                        :max-length="1024"
                      />
                    </div>

                    <div
                      v-if="bodyVariables.length"
                      class="flex flex-col gap-2"
                    >
                      <span class="text-sm font-medium text-n-slate-12">
                        {{
                          t(
                            'WHATSAPP_TEMPLATE_MGMT.CREATE.CONTENT.VARIABLES_TITLE'
                          )
                        }}
                      </span>
                      <p class="m-0 text-xs text-n-slate-11">
                        {{
                          t(
                            'WHATSAPP_TEMPLATE_MGMT.CREATE.CONTENT.VARIABLES_HELP'
                          )
                        }}
                      </p>
                      <p
                        v-if="variablesError === 'ORDER'"
                        class="m-0 text-xs text-n-ruby-9"
                      >
                        {{
                          t(
                            'WHATSAPP_TEMPLATE_MGMT.CREATE.CONTENT.VARIABLES_ORDER'
                          )
                        }}
                      </p>
                      <div class="grid gap-2 sm:grid-cols-2">
                        <Input
                          v-for="variable in bodyVariables"
                          :key="variable"
                          v-model="variableExamples[variable]"
                          :label="`{{${variable}}}`"
                          :placeholder="
                            t(
                              'WHATSAPP_TEMPLATE_MGMT.CREATE.CONTENT.VARIABLE_EXAMPLE_PLACEHOLDER'
                            )
                          "
                        />
                      </div>
                    </div>

                    <div class="flex flex-col gap-1">
                      <span class="text-sm font-medium text-n-slate-12">
                        {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.CONTENT.FOOTER') }}
                      </span>
                      <Input
                        v-model="draft.footerText"
                        :placeholder="
                          t(
                            'WHATSAPP_TEMPLATE_MGMT.CREATE.CONTENT.FOOTER_PLACEHOLDER'
                          )
                        "
                        :maxlength="FOOTER_LIMIT"
                        :message="`${draft.footerText.length}/${FOOTER_LIMIT}`"
                        :message-type="
                          draft.footerText.length > FOOTER_LIMIT
                            ? 'error'
                            : 'info'
                        "
                      />
                      <PhraseShortcuts
                        :phrases="footerPhrases"
                        :model-value="draft.footerText"
                        @apply="draft.footerText = $event"
                        @preview="previewPhrase('footer', $event)"
                      />
                    </div>
                  </section>

                  <!-- Step 3: Buttons -->
                  <TemplateButtonsEditor
                    v-else-if="currentStep === 2"
                    key="buttons"
                    v-model="draft.buttons"
                    :category="draft.category"
                    @preview="
                      previewPhrase('button', $event?.text, $event?.index)
                    "
                  />

                  <!-- Step 4: Review -->
                  <section v-else key="review" class="flex flex-col gap-5">
                    <dl class="grid gap-3 m-0 sm:grid-cols-2">
                      <div class="sm:col-span-2">
                        <dt class="text-xs text-n-slate-11">
                          {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.REVIEW.INBOX') }}
                        </dt>
                        <dd class="m-0 text-sm font-medium text-n-slate-12">
                          {{ inbox?.name }}
                          <span
                            v-if="inbox?.phone_number"
                            class="ml-1 font-normal text-n-slate-11"
                          >
                            {{ inbox.phone_number }}
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt class="text-xs text-n-slate-11">
                          {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.REVIEW.NAME') }}
                        </dt>
                        <dd class="m-0 text-sm font-medium text-n-slate-12">
                          {{ draft.name }}
                        </dd>
                      </div>
                      <div>
                        <dt class="text-xs text-n-slate-11">
                          {{
                            t('WHATSAPP_TEMPLATE_MGMT.CREATE.REVIEW.CATEGORY')
                          }}
                        </dt>
                        <dd class="m-0 text-sm font-medium text-n-slate-12">
                          {{ draft.category }}
                        </dd>
                      </div>
                      <div>
                        <dt class="text-xs text-n-slate-11">
                          {{
                            t('WHATSAPP_TEMPLATE_MGMT.CREATE.REVIEW.LANGUAGE')
                          }}
                        </dt>
                        <dd class="m-0 text-sm font-medium text-n-slate-12">
                          {{ formatTemplateLanguage(draft.language) }}
                        </dd>
                      </div>
                      <div>
                        <dt class="text-xs text-n-slate-11">
                          {{
                            t('WHATSAPP_TEMPLATE_MGMT.CREATE.REVIEW.BUTTONS')
                          }}
                        </dt>
                        <dd class="m-0 text-sm font-medium text-n-slate-12">
                          {{
                            draft.buttons.length ||
                            t('WHATSAPP_TEMPLATE_MGMT.CREATE.REVIEW.NONE')
                          }}
                        </dd>
                      </div>
                    </dl>
                    <div
                      class="flex gap-3 p-3 border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-11"
                    >
                      <Icon
                        icon="i-lucide-clock"
                        class="flex-shrink-0 size-4 mt-0.5 text-n-amber-11"
                      />
                      <p class="m-0 text-sm">
                        {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.REVIEW.NOTICE') }}
                      </p>
                    </div>
                  </section>
                </Transition>
              </div>
            </div>

            <!-- The actions belong to the form: they close the card, and stay in reach while it scrolls. -->
            <div
              class="sticky bottom-0 z-10 flex items-center justify-between gap-3 px-6 py-3 border-t border-n-weak bg-n-solid-1 rounded-b-xl"
            >
              <NextButton
                v-if="!isAssistantOpen"
                type="button"
                variant="faded"
                color="slate"
                :disabled="isFirstStep"
                @click="goBack"
              >
                {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.BACK') }}
              </NextButton>
              <span v-else />
              <div class="flex items-center gap-2">
                <NextButton
                  type="button"
                  variant="ghost"
                  color="slate"
                  @click="goToTemplatesTab"
                >
                  {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.CANCEL') }}
                </NextButton>
                <NextButton
                  v-if="isAssistantOpen"
                  type="button"
                  :disabled="isAssistantBusy || !draft.bodyText.trim()"
                  @click="goToStep(STEPS.length - 1)"
                >
                  {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.ASSISTANT.REVIEW') }}
                </NextButton>
                <NextButton
                  v-else-if="!isLastStep"
                  type="button"
                  :disabled="!canContinue"
                  @click="goNext"
                >
                  {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.NEXT') }}
                </NextButton>
                <NextButton
                  v-else
                  type="button"
                  color="teal"
                  :is-loading="isSubmitting"
                  :disabled="isSubmitting"
                  @click="submit"
                >
                  {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.SUBMIT') }}
                </NextButton>
              </div>
            </div>
          </div>

          <!-- Live preview -->
          <aside class="flex flex-col gap-2 lg:sticky lg:top-6">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW_TITLE') }}
            </span>
            <TemplateChatPreview
              :name="inbox?.name"
              :avatar-url="inbox?.avatar_url"
              :header-text="previewHeaderText"
              :body-parts="previewBodyParts"
              :footer-text="previewFooterText"
              :buttons="previewButtons"
              :highlight="phrasePreview?.part"
              @select="goToStep(PREVIEW_PART_STEPS[$event])"
            />
            <p class="flex gap-1.5 m-0 text-xs text-n-slate-11">
              <Icon
                icon="i-lucide-mouse-pointer-click"
                class="flex-shrink-0 size-3.5 mt-px"
              />
              {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW_HINT') }}
            </p>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>
